import { Trans, useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import { AssignLevelForm } from '@/pages/console/hierarchy/AssignLevelForm';
import type { ScopedAccountsVM } from './useScopedAccounts';
import type { ScopedAccountWritesVM } from './useScopedAccountWrites';

/**
 * EAA-1839: the three single-row dialogs of the scoped accounts browser —
 * "+ Tài khoản" (grant an existing account into this đơn vị), the revoke confirm
 * and the reset-password confirm — plus the one-time temp-password panel.
 *
 * WHY "+ TÀI KHOẢN" IS A GRANT, NOT AN ACCOUNT CREATE. Creating a Keycloak identity
 * is `POST /accounts`, which is super-admin only and — decisively — produces no
 * `Assignment`, so a freshly created account would not appear in this list at all.
 * The write that adds a row here is `POST /organizations/{id}/assignments`, the
 * same endpoint `/console/hierarchy`'s "Cấp quyền" modal already uses. That modal
 * could not be reused as-is: it is bound to `HierarchyPageVM`, and lifting it would
 * mean refactoring the tree view model this ticket must not touch. The form is
 * therefore the same three fields against the same single endpoint, with a hint
 * saying plainly that the account must already exist.
 */
export function ScopedAccountDialogs({
    vm,
    writes,
}: {
    vm: ScopedAccountsVM;
    writes: ScopedAccountWritesVM;
}) {
    const { t } = useTranslation();
    const revokeTarget = writes.revokeTarget;
    const resetTarget = writes.resetTarget;
    const revokeIdentity = revokeTarget?.user_email || revokeTarget?.user_sub;
    const resetIdentity = resetTarget?.user_email || resetTarget?.user_sub;

    return (
        <>
            {vm.canManage && (
                <Modal
                    open={writes.addOpen}
                    onCancel={writes.closeAdd}
                    title={t('console.hierarchy.scopedAccounts.dialogs.addTitle')}
                    footer={
                        <>
                            <button
                                type="button"
                                className="portal-btn"
                                onClick={writes.closeAdd}
                                disabled={writes.addBusy}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                form="scoped-accounts-add-form"
                                className="portal-btn portal-btn-primary"
                                disabled={writes.addBusy || writes.addLevels.length === 0}
                            >
                                {writes.addBusy
                                    ? t('console.hierarchy.scopedAccounts.dialogs.addSubmitBusy')
                                    : t(
                                          'console.hierarchy.scopedAccounts.dialogs.addSubmitButton'
                                      )}
                            </button>
                        </>
                    }
                >
                    <p className="portal-admin-hint">
                        <Trans
                            i18nKey="console.hierarchy.scopedAccounts.dialogs.addHint"
                            components={{ bold: <strong /> }}
                        />
                    </p>
                    {/* EAA-1839: same fields, same endpoint, one component — see
                        `AssignLevelForm` for why the tree's "Cấp quyền" modal and this
                        one share the body instead of each declaring the request shape. */}
                    <AssignLevelForm
                        formId="scoped-accounts-add-form"
                        value={writes.addForm}
                        onChange={writes.setAddForm}
                        levels={writes.addLevels}
                        error={writes.addError}
                        onSubmit={writes.submitAdd}
                    />
                </Modal>
            )}

            <Modal
                open={Boolean(revokeTarget)}
                onCancel={() => writes.setRevokeTarget(null)}
                title={t('console.hierarchy.scopedAccounts.dialogs.revokeTitle')}
                footer={
                    <>
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={() => writes.setRevokeTarget(null)}
                            disabled={writes.revokeBusy}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-danger"
                            onClick={() => {
                                if (revokeTarget) writes.runRevoke(revokeTarget);
                            }}
                            disabled={writes.revokeBusy}
                        >
                            {writes.revokeBusy
                                ? t('console.hierarchy.scopedAccounts.dialogs.revokeBusy')
                                : t('console.hierarchy.scopedAccounts.dialogs.revokeButton')}
                        </button>
                    </>
                }
            >
                <p className="portal-confirm-message">
                    {t('console.hierarchy.scopedAccounts.dialogs.revokeConfirmMessage', {
                        identity: revokeIdentity,
                    })}
                </p>
            </Modal>

            {vm.canReset && (
                <Modal
                    open={Boolean(resetTarget)}
                    onCancel={() => writes.setResetTarget(null)}
                    title={t('console.hierarchy.scopedAccounts.dialogs.resetTitle')}
                    footer={
                        <>
                            <button
                                type="button"
                                className="portal-btn"
                                onClick={() => writes.setResetTarget(null)}
                                disabled={writes.resetBusy}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="portal-btn portal-btn-primary"
                                onClick={() => {
                                    if (resetTarget) writes.runReset(resetTarget);
                                }}
                                disabled={writes.resetBusy}
                            >
                                {writes.resetBusy
                                    ? t('console.hierarchy.scopedAccounts.dialogs.resetBusy')
                                    : t('console.hierarchy.scopedAccounts.dialogs.resetButton')}
                            </button>
                        </>
                    }
                >
                    <p className="portal-admin-hint">
                        <Trans
                            i18nKey="console.hierarchy.scopedAccounts.dialogs.resetHint"
                            values={{ identity: resetIdentity }}
                            components={{ bold: <strong /> }}
                        />
                    </p>
                    <p className="portal-accounts-onetime-warning">
                        {t('console.hierarchy.scopedAccounts.dialogs.resetOneTimeWarning')}
                    </p>
                </Modal>
            )}

            {writes.resetResult && (
                <div className="portal-admin-secret" role="alert">
                    <strong>{t('console.hierarchy.scopedAccounts.dialogs.resetResultHeading')}</strong>
                    <span>
                        {t('console.hierarchy.scopedAccounts.dialogs.usernameLabel')}{' '}
                        <code>{writes.resetResult.username}</code>
                    </span>
                    <span>
                        {t('console.hierarchy.scopedAccounts.dialogs.tempPasswordLabel')}{' '}
                        <code>{writes.resetResult.temp_password}</code>
                    </span>
                    <button type="button" onClick={writes.dismissResetResult}>
                        {t('console.hierarchy.scopedAccounts.dialogs.dismissResetResult')}
                    </button>
                </div>
            )}
        </>
    );
}
